// src/pages/RepPositionsPage.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PublicIcon from "@mui/icons-material/Public";
import API from "../utils/api";
import { toast } from "react-toastify";
import useAuth from "../hooks/useAuth";

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Custom user position icon
const createUserPin = (color = "#1976d2") =>
  L.divIcon({
    className: "user-pin",
    html: `<div style="
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: ${color};
      border: 3px solid #fff;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 12px;
    ">👤</div>`,
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });

// Fit bounds helper - tracks manual zoom to prevent auto-fit after user interaction
function FitBoundsOnData({ activities, userZoomedRef, initialFitRef, territoryChangedRef }) {
  const map = useMap();
  const isFittingRef = React.useRef(false);

  useEffect(() => {
    // Stop auto-fit after user zooms manually
    if (userZoomedRef.current) return;

    // Only auto-fit on first load or territory filter change
    if (!initialFitRef.current && !territoryChangedRef.current) return;

    if (activities.length === 0) return;

    const validActivities = activities.filter(
      (a) => a.latitude != null && a.longitude != null
    );
    if (validActivities.length === 0) return;

    // Mark that we're about to fit bounds (to distinguish from user zoom)
    isFittingRef.current = true;

    const bounds = L.latLngBounds(
      validActivities.map((a) => [a.latitude, a.longitude])
    );

    map.fitBounds(bounds, { padding: [50, 50] });
    
    // Mark as fitted
    initialFitRef.current = false;
    territoryChangedRef.current = false;
    
    // Reset fitting flag after a short delay
    setTimeout(() => {
      isFittingRef.current = false;
    }, 500);
  }, [activities, map, userZoomedRef, initialFitRef, territoryChangedRef]);

  // Listen for manual zoom/pan to disable auto-fit
  useMapEvents({
    zoomend: () => {
      // Only mark as user zoomed if it wasn't from fitBounds
      if (!isFittingRef.current) {
        userZoomedRef.current = true;
      }
    },
    moveend: () => {
      // Only mark as user zoomed if it wasn't from fitBounds
      if (!isFittingRef.current) {
        userZoomedRef.current = true;
      }
    },
  });

  return null;
}


export default function RepPositionsPage() {
  const theme = useTheme();
  const { role } = useAuth();
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [selectedTerritory, setSelectedTerritory] = useState("");
  const [loading, setLoading] = useState(true);

  // Refs for tracking zoom state
  const userZoomedRef = useRef(false);
  const initialFitRef = useRef(true);
  const territoryChangedRef = useRef(false);
  const prevTerritoryRef = useRef(selectedTerritory);

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get("rep-activity/");
      const data = res.data || [];
      setActivities(data);
      setFilteredActivities(data);
    } catch (err) {
      console.error("Error fetching rep activities:", err);
      toast.error("Failed to load rep positions.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTerritories = useCallback(async () => {
    try {
      const res = await API.get("territories/");
      setTerritories(res.data || []);
    } catch (err) {
      console.error("Error fetching territories:", err);
    }
  }, []);

  useEffect(() => {
    fetchActivities();
    fetchTerritories();
    // Refresh every 30 seconds
    const interval = setInterval(fetchActivities, 30000);
    return () => clearInterval(interval);
  }, [fetchActivities, fetchTerritories]);

  useEffect(() => {
    // Track territory filter changes - allow auto-fit when filter changes
    if (prevTerritoryRef.current !== selectedTerritory) {
      territoryChangedRef.current = true;
      // Reset user zoom when filter changes (allow auto-fit again)
      userZoomedRef.current = false;
      prevTerritoryRef.current = selectedTerritory;
    }

    if (!selectedTerritory) {
      setFilteredActivities(activities);
      return;
    }
    const filtered = activities.filter((a) => {
      const territoryId = a.territory?.id || a.territory;
      return territoryId === Number(selectedTerritory);
    });
    setFilteredActivities(filtered);
  }, [selectedTerritory, activities]);

  const formatLastActive = (dateString) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getStatusColor = (lastActive) => {
    if (!lastActive) return "default";
    const date = new Date(lastActive);
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);
    if (diffMins < 15) return "success";
    if (diffMins < 60) return "warning";
    return "error";
  };

  const validActivities = filteredActivities.filter(
    (a) => a.latitude != null && a.longitude != null

  );

  if (loading && activities.length === 0) {
    return (
      <Box sx={{ p: 3, px: { xs: 1, sm: 3 } }}>
        <Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
          {/* Header Card */}
          <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(25,118,210,0.08)",
                  }}
                >
                  <LocationOnIcon color="primary" />
                </Box>

                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Rep Positions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View real-time locations of sales representatives
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Card>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <CircularProgress />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, px: { xs: 1, sm: 3 } }}>
      <Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
        {/* Header Card */}
        <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <LocationOnIcon color="primary" />
              </Box>

              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Rep Positions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View real-time locations of sales representatives
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Card>

        <Stack spacing={2}>
        {/* ----------------------------------------------------------Filter */}
        <Box
          sx={{ display: "flex", gap: 2, alignItems: "center" }}
          data-tour="rep-positions-filter"
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Territory</InputLabel>
            <Select
              value={selectedTerritory}
              label="Filter by Territory"
              onChange={(e) => setSelectedTerritory(e.target.value)}
            >
              <MenuItem value="">All Territories</MenuItem>
              {territories.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Chip
            icon={<PersonIcon />}
            label={`${validActivities.length} Active Reps`}
            color="primary"
            variant="outlined"
          />
        </Box>

        {/* --------------------------------------------------------Map */}
        <Paper
          data-tour="rep-positions-map"
          elevation={2}
          sx={{
            height: { xs: "500px", md: "600px" },
            width: "100%",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {validActivities.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <LocationOnIcon sx={{ fontSize: 64, color: "text.secondary" }} />
              <Typography variant="h6" color="text.secondary">
                No position data available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reps need to grant location permissions for tracking
              </Typography>
            </Box>
          ) : (
            <MapContainer
              center={[validActivities[0]?.latitude || 39.8283, validActivities[0]?.longitude || -98.5795]}
              zoom={6}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBoundsOnData 
                activities={validActivities} 
                userZoomedRef={userZoomedRef}
                initialFitRef={initialFitRef}
                territoryChangedRef={territoryChangedRef}
              />
              {validActivities.map((activity) => (
                <Marker
                  key={activity.id}
                  position={[activity.latitude, activity.longitude]}
                  icon={createUserPin("#1976d2")}
                >
                  <Popup>
                    <Card sx={{ minWidth: 200 }}>
                      <CardContent>
                        <Stack spacing={1}>
                          <Box>
                            <Typography variant="h6" fontWeight={700}>
                              {activity.username || "Unknown User"}
                            </Typography>
                            {activity.territory_name && (
                              <Chip
                                icon={<PublicIcon />}
                                label={activity.territory_name}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <AccessTimeIcon fontSize="small" color="action" />
                           
                            <Chip
                              label={formatLastActive(activity.last_active)}
                              size="small"
                              color={getStatusColor(activity.last_active)}
                              sx={{ ml: "auto" }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Lat: {activity.latitude?.toFixed(4)}, Lng: {activity.longitude?.toFixed(4)}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </Paper>

        {/* -----------------------------------------------------------Activity List */}
        {filteredActivities.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                All Rep Activity
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {filteredActivities.map((activity) => (
                  <Paper
                    key={activity.id}
                    sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {activity.username || "Unknown User"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {activity.territory_name || "No territory"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      {activity.latitude != null && activity.longitude != null ? (
                        <Chip
                          icon={<LocationOnIcon />}
                          label="Has Location"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          label="No Location"
                          color="default"
                          size="small"
                        />
                      )}
                      <Chip
                        label={formatLastActive(activity.last_active)}
                        size="small"
                        color={getStatusColor(activity.last_active)}
                      />
                    </Box>
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}
        </Stack>
      </Box>
    </Box>
  );
}